import { useState } from 'react';
import styled, { css } from 'styled-components'
import { useProduct } from '../../contexts/ProductProvider';
import { Breakpoint, Columns, MobileBreakpoint } from '../../styles/functions';
import { BezierDefault, Hover, TypeBodyM, TypeCaptionM, TypeHeadingXS } from '../../styles/mixins';
import LinkRender from '../link/LinkRender';
import SanityContent from '../sanity/content/SanityContent';
import SanityImage from '../sanity/SanityImage';
import SanityMedia from '../sanity/SanityMedia';
import SvgRightUp from '../svg/SvgRightUp';
import ProductSpecs from './ProductSpecs';

const ProductPacking: React.FC = ({ 
  ...props
}) => {
  const { product, shopifyProduct } = useProduct();
  const {packing} = product;
  const [activeIndex, setActiveIndex] = useState(-1);

  return (
    <Container {...props}>
      <Visual>
        <Thumbnails>
          {
            packing.items.map((item, index) => item.thumbnail &&
              <Thumbnail
              alt={item.thumbnail?.alt || ''} 
                $isActive={index == activeIndex} 
                image={item.thumbnail} 
                ratio="3x4"
                key={item._key} 
              />
            )
          }
        </Thumbnails>
        <Media 
          media={packing.media} 
          ratio="1x1" 
          autoplay={true} 
          layout="fill"
          objectFit="cover"
        />
      </Visual>
      <Content>
        <div style={{marginBottom: '8px'}} data-anchor="packing">The Art of Packing</div>
        <Introduction layout="DEFAULT" blocks={packing.introduction} />
        <Details>
          <ProductSpecs
            withSku={false}
            withStyle={false}
            withColour={false}
            withColours={false}
            withHandle={true}
            withSpecs={true} 
          />
          <ul onMouseOut={() => setActiveIndex(-1)}>
            {
              packing.items.map((item, index) => 
                <ItemLinkWrap 
                  $isInactive={activeIndex > -1 && activeIndex != index} 
                  onMouseOver={() => setActiveIndex(index)}
                  key={item._key}
                >
                  { 
                    item.link.toggle ? 
                    <ItemLink href={item.link.href}>
                      {item.title}
                      <SvgRightUp />
                    </ItemLink> : 
                    <span>{item.title}</span>
                  }
                </ItemLinkWrap>
              )
            }
          </ul>
        </Details>
        <Packing>
          <Diagram>
            {
              packing.items.map((item, index) => 
                <Item
                  alt={`Item ${index+1}`}
                  $isActive={index == activeIndex} 
                  image={item.diagram} 
                  key={item._key} 
                />
              )
            }
            <SanityImage alt="Background Diagram" image={packing.backgroundDiagram} />
          </Diagram>

          <Sizes>
            {
              packing.items.map((item, index) => item.specs &&
                <Size
                  layout="DEFAULT"
                  $isActive={index == activeIndex} 
                  blocks={item.specs}
                  key={item._key} 
                />
              )
            }
          </Sizes>
        </Packing>
      </Content>
    </Container>
  )
}

const Container = styled.div`
  ${TypeBodyM};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 var(--gutter);
  counter-reset: caption;
  padding-top: var(--spacing-120);
  padding-bottom: var(--spacing-40);

  ${MobileBreakpoint} {
    display: flex;
    flex-direction: column;
  }
`

const Visual = styled.div`
  position: relative;
  /* min-height: 500px; */
  min-height: ${Columns(12)};

  ${MobileBreakpoint} {
    min-height: 500px;
    margin-bottom: 8px;
  }
`

const Media = styled(SanityMedia)`
  display: contents;
  > * {
    position: absolute;
    left:0;right:0;
    top:0;bottom:0;
  }
`

const Thumbnails = styled.div`
  display: grid;
  position: absolute;
  left: 16px;
  bottom: 16px;
  width: 150px;
  z-index: 10;

  ${MobileBreakpoint} {
    display: none;
  }

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    margin-top: auto;
  }
`

const Thumbnail = styled(SanityImage)<{$isActive}>`
  opacity: 0;
  transition: opacity 0.2s ease;
  border: 1px solid var(--color-charcoal300);

  ${props => props.$isActive && css`
    opacity: 1;
  `}
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--color-charcoal100);
  padding: var(--spacing-16);
  justify-content: space-between;

  ${MobileBreakpoint} {
    min-height: 500px;
  }
`

const Introduction = styled(SanityContent)`
  ${TypeHeadingXS};
  margin-bottom: var(--spacing-24);
`

const Details = styled.div`
  display: grid;
  grid-template-columns: ${Columns(7,-2)} ${Columns(5,-2)};
  /* padding-bottom: var(--spacing-40); */

  ${MobileBreakpoint} {
    grid-template-columns: 1fr 1fr;
  }
`

const ItemLinkWrap = styled.li<{$isInactive}>`
  position: relative;
  padding-left: 48px;
  cursor: pointer;

  ${MobileBreakpoint} {
    padding-left: 32px;
  }

  &:before {
    ${TypeCaptionM};
    display: inline-flex;
    content: counter( caption , decimal-leading-zero) ' ';
    counter-increment: caption;
    position: absolute;
    left: 0;
    top: 2px;
  }

  > * {
    transition: color 0.3s ${BezierDefault};;
  }

  ${props => props.$isInactive && css`
    > * {
      color: var(--color-charcoal400);
    }
  `}
`

const ItemLink = styled(LinkRender)`
  position: relative;
  svg {
    position: absolute;
    left: calc(100% + 10px);
    top: 7px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  ${Hover} {
    svg {
      opacity:1;
    }
  }
`

const Packing = styled.div`
  margin-bottom: 0;
  margin-top: auto;
  position: relative;
`

const Diagram = styled.div`
  width: ${Columns(8)};
  margin: auto;
  display: grid;
  /* margin-bottom: 20px; */

  ${MobileBreakpoint} {
    width: ${Columns(8)};
    margin-bottom: 32px;
  }

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }
`

const Item = styled(SanityImage)<{$isActive}>`
  opacity: 0.3;
  transition: opacity 0.3s ${BezierDefault};

  ${props => props.$isActive && css`
    opacity: 1;
  `}
`

const Sizes = styled.div`
  display: grid;
  position: absolute;
  left: 0;
  bottom: 0;
  /* width: 150px; */
  z-index: 10;

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    margin-top: auto;
  }
`

const Size = styled(SanityContent)<{$isActive}>`
  opacity: 0;
  transition: opacity 0.2s ease;

  ${props => props.$isActive && css`
    opacity: 1;
  `}
`


export default ProductPacking